import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { Map, View } from 'ol';
import { fromLonLat } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import XYZ from 'ol/source/XYZ';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { Style, Circle, Fill, Stroke, Text } from 'ol/style';
import { Overlay } from 'ol';
import 'ol/ol.css';
import './VWorldMap.css';

// 초기 좌표 상수 (컴포넌트 외부에 정의하여 의존성 배열 문제 해결)
const INITIAL_CENTER = [128.0, 35.8];
const INITIAL_ZOOM = 7.2;
const INITIAL_ZOOM_MOBILE = 6.5; // 모바일용 더 작은 줌 레벨

// 모바일 여부 확인 함수
const isMobile = () => {
  return window.innerWidth <= 768;
};

const VWorldMap = ({ plants, selectedPlant, onPlantSelect, getStatusColor }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const vectorSourceRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // VWorld API 키 및 도메인 (환경 변수에서 가져옴)
  // 환경 변수가 없으면 개발 환경에서만 경고 표시
  const VWORLD_API_KEY = process.env.REACT_APP_VWORLD_API_KEY;
  const VWORLD_DOMAIN = process.env.REACT_APP_VWORLD_DOMAIN;
  
  // 환경 변수 검증 (개발 환경에서만)
  if (process.env.NODE_ENV === 'development') {
    if (!VWORLD_API_KEY) {
      console.warn('⚠️ REACT_APP_VWORLD_API_KEY 환경 변수가 설정되지 않았습니다.');
    }
    if (!VWORLD_DOMAIN) {
      console.warn('⚠️ REACT_APP_VWORLD_DOMAIN 환경 변수가 설정되지 않았습니다.');
    }
  }

  // 지도 컨테이너가 렌더링된 후 실행 (useLayoutEffect는 DOM 업데이트 직후 동기적으로 실행됨)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // getStatusColor와 onPlantSelect는 이벤트 핸들러에서만 사용되므로 의존성 배열에 포함하지 않음
  // 지도 초기화는 한 번만 실행되어야 함
  useLayoutEffect(() => {
    // mapRef가 설정될 때까지 대기
    if (!mapRef.current) {
      console.log('mapRef.current가 아직 설정되지 않음');
      return;
    }

    console.log('지도 초기화 useLayoutEffect 실행됨, mapRef.current:', mapRef.current);

    // 환경 변수 검증
    if (!VWORLD_API_KEY || !VWORLD_DOMAIN) {
      console.error('❌ VWorld API 키 또는 도메인이 설정되지 않았습니다.');
      console.error('REACT_APP_VWORLD_API_KEY:', VWORLD_API_KEY ? '설정됨' : '미설정');
      console.error('REACT_APP_VWORLD_DOMAIN:', VWORLD_DOMAIN ? '설정됨' : '미설정');
      setMapLoaded(true); // 로딩 상태 해제
      return;
    }

    // VWorld JavaScript API 스크립트 로드
    const scriptId = 'vworld-map-script';
    let script = document.getElementById(scriptId);
    
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://map.vworld.kr/js/vworldMapInit.js.do?version=2.0&apiKey=${VWORLD_API_KEY}&domain=${VWORLD_DOMAIN}`;
      script.async = true;
      document.head.appendChild(script);
    }

    // 스크립트 로드 후 지도 초기화
    let retryCount = 0;
    const maxRetries = 100; // 최대 10초 대기
    
    const initMap = () => {
      console.log('initMap 호출됨, retryCount:', retryCount);
      
      // VWorld API가 로드되지 않았으면 대기
      if (!window.vw || !window.vw.ol3) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(initMap, 100);
        } else {
          console.error('VWorld API 로드 실패: window.vw 또는 window.vw.ol3를 찾을 수 없습니다.');
          console.log('현재 window.vw:', window.vw);
          setMapLoaded(true); // 로딩 상태 해제
        }
        return;
      }

      console.log('VWorld API 로드 확인됨, 지도 초기화 시작');
      const vw = window.vw;
      
      // VWorld API 구조 확인
      console.log('VWorld API 구조:', {
        vw: vw,
        ol3: vw.ol3,
        BasemapType: vw.ol3?.BasemapType,
        Map: vw.ol3?.Map,
        Coordinate: vw.ol3?.Coordinate,
        CameraPosition: vw.ol3?.CameraPosition,
        keys: Object.keys(vw.ol3 || {})
      });
      
      try {
      // OpenLayers를 직접 사용 (VWorld가 OpenLayers를 래핑하므로)
      // 설치된 ol 패키지 사용
      console.log('OpenLayers 모듈 로드 확인됨');

      // VWorld 타일 레이어 URL 가져오기
      // VWorld API 2.0 타일 URL 형식 확인 필요
      // 일반적으로 {z}/{x}/{y} 또는 {z}/{y}/{x} 형식 사용
      let baseMapUrl = vw.ol3?.MapUrls?.base;
      if (!baseMapUrl) {
        // VWorld 기본 타일 URL (Base 지도)
        baseMapUrl = 'https://cdn.vworld.kr/2d/Base/service/';
      }
      
      // URL이 슬래시로 끝나지 않으면 추가
      if (!baseMapUrl.endsWith('/')) {
        baseMapUrl += '/';
      }
      
      console.log('VWorld 타일 URL:', baseMapUrl);
      
      // OpenLayers 지도 생성
      console.log('지도 생성 시작, 컨테이너 ID:', mapRef.current.id || 'vworld-map');
      const mapContainerId = mapRef.current.id || 'vworld-map';
      
      // VWorld 타일 소스 생성
      // VWorld는 일반적으로 {z}/{x}/{y} 형식 사용 (OpenLayers XYZ 표준)
      // 만약 타일이 깨져 보이면 {z}/{y}/{x} 형식도 시도해볼 수 있음
      const tileSource = new XYZ({
        url: baseMapUrl + '{z}/{x}/{y}.png',
        tileSize: 256,
        maxZoom: 18,
        projection: 'EPSG:3857' // Web Mercator 좌표계 명시
      });
      
      console.log('타일 소스 URL 패턴:', baseMapUrl + '{z}/{x}/{y}.png');

      const tileLayer = new TileLayer({
        source: tileSource
      });

      // 한국 중심 좌표
      const koreaCenter = fromLonLat([128.0, 35.8], 'EPSG:3857'); // 경도 증가(오른쪽), 위도 감소(아래)
      
      // 모바일 여부에 따라 줌 레벨 결정
      const initialZoom = isMobile() ? INITIAL_ZOOM_MOBILE : INITIAL_ZOOM;
      
      // OpenLayers 지도 생성
      const olMap = new Map({
        target: mapContainerId,
        layers: [tileLayer],
        view: new View({
          center: koreaCenter,
          zoom: initialZoom,
          projection: 'EPSG:3857' // 좌표계 명시
        })
      });
      
      console.log('지도 초기화 완료, 중심 좌표:', koreaCenter, '줌 레벨: 7.5');

      // VWorld Map 래퍼 생성 (VWorld API가 제공하는 경우)
      let vmap = null;
      if (vw.ol3?.Map) {
        try {
          // VWorld MapOptions 설정 시도
          const mapOptions = {
            basemapType: vw.ol3.BasemapType?.GRAPHIC || 'Base',
            controlDensity: vw.ol3.DensityType?.EMPTY || 'empty',
            interactionDensity: vw.ol3.DensityType?.BASIC || 'basic',
            controlsAutoArrange: true
          };
          vmap = new vw.ol3.Map(mapContainerId, mapOptions);
        } catch (e) {
          console.warn('VWorld Map 래퍼 생성 실패, OpenLayers 직접 사용:', e);
          // OpenLayers를 직접 사용
          vmap = {
            getMap: () => olMap,
            setCameraPosition: (cameraPosition) => {
              if (cameraPosition && cameraPosition.coordinate) {
                const coord = cameraPosition.coordinate;
                olMap.getView().setCenter(coord);
                if (cameraPosition.zoom) {
                  olMap.getView().setZoom(cameraPosition.zoom);
                }
              }
            },
            destroy: () => {
              olMap.setTarget(null);
            }
          };
        }
      } else {
        // VWorld Map이 없으면 OpenLayers 직접 사용
        vmap = {
          getMap: () => olMap,
          setCameraPosition: (cameraPosition) => {
            if (cameraPosition && cameraPosition.coordinate) {
              const coord = cameraPosition.coordinate;
              olMap.getView().setCenter(coord);
              if (cameraPosition.zoom) {
                olMap.getView().setZoom(cameraPosition.zoom);
              }
            }
          },
          destroy: () => {
            olMap.setTarget(null);
          }
        };
      }
      mapInstanceRef.current = vmap;
      console.log('지도 생성 완료, mapLoaded 상태 변경');
      
      // 지도 초기화 완료 후 약간의 지연을 두고 mapLoaded 상태 변경
      // 이렇게 하면 마커 업데이트 useEffect가 제대로 실행됨
      setTimeout(() => {
        setMapLoaded(true);
        console.log('mapLoaded 상태 변경 완료, 마커 업데이트 useEffect 실행 예정');
      }, 100);

      // 팝업 오버레이 생성
      const popupElement = document.createElement('div');
      popupElement.className = 'vworld-popup-container';
      const popup = new Overlay({
        element: popupElement,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -10],
      });
      olMap.addOverlay(popup);
      popupRef.current = popup;

      // 마커 레이어 생성
      const vectorSource = new VectorSource();
      vectorSourceRef.current = vectorSource; // ref에 저장하여 외부에서 접근 가능하게
      const vectorLayer = new VectorLayer({
        source: vectorSource,
      });
      olMap.addLayer(vectorLayer);

      // 지도 초기화 완료 후 초기 마커 추가 (plants가 이미 로드된 경우)
      // 나머지는 별도 useEffect에서 처리

      // 마커 클릭 이벤트 처리
      olMap.on('click', (event) => {
        let clicked = false;
        olMap.forEachFeatureAtPixel(event.pixel, (feature) => {
          if (!clicked) {
            const plant = feature.get('plant');
            if (plant) {
              onPlantSelect(plant);
              clicked = true;
            }
          }
          return true;
        });
        
        // 클릭한 곳이 마커가 아니면 팝업 닫기
        if (!clicked) {
          popup.setPosition(undefined);
        }
      });

      // 마커에 마우스 오버 시 포인터 변경 및 팝업 표시
      olMap.on('pointermove', (event) => {
        const hit = olMap.forEachFeatureAtPixel(event.pixel, (feature) => {
          const plant = feature.get('plant');
          if (plant) {
            const element = popup.getElement();
            if (element) {
              element.innerHTML = `
                <div class="vworld-popup">
                  <h4>${plant.name}</h4>
                  <div class="popup-info">
                    <div class="popup-item">
                      <span class="label">발전량:</span>
                      <span class="value">${typeof plant.powerGeneration === 'number' ? Math.round(plant.powerGeneration).toLocaleString() : plant.powerGeneration} kW</span>
                    </div>
                    <div class="popup-item">
                      <span class="label">효율:</span>
                      <span class="value">${plant.efficiency}%</span>
                    </div>
                    <div class="popup-item">
                      <span class="label">통신 상태:</span>
                      <span class="value" style="color: ${getStatusColor(plant.status)}">
                        ${plant.communicationStatus === 'normal' ? '정상' : '끊김'}
                      </span>
                    </div>
                  </div>
                </div>
              `;
              const coordinates = feature.getGeometry().getCoordinates();
              popup.setPosition(coordinates);
            }
          }
          return true;
        });
        
        olMap.getViewport().style.cursor = hit ? 'pointer' : '';
        
        // 마커 위에 있지 않으면 팝업 숨기기
        if (!hit) {
          popup.setPosition(undefined);
        }
      });
      
      console.log('지도 초기화 완료');
      } catch (error) {
        console.error('지도 초기화 오류:', error);
        console.error('에러 스택:', error.stack);
        setMapLoaded(true); // 에러가 발생해도 로딩 상태 해제
      }
    };

    // 스크립트 로드 대기
    const checkAndInit = () => {
      console.log('checkAndInit 호출됨');
      console.log('현재 상태:', {
        vw: !!window.vw,
        ol3: !!(window.vw && window.vw.ol3),
        scriptComplete: script.complete,
        scriptSrc: script.src
      });
      
      if (window.vw && window.vw.ol3) {
        console.log('VWorld API 이미 로드됨, 바로 초기화');
        initMap();
      } else {
        console.log('VWorld API 대기 중...');
        script.onload = () => {
          console.log('스크립트 로드 완료, 지도 초기화 시작');
          setTimeout(() => {
            if (window.vw && window.vw.ol3) {
              initMap();
            } else {
              console.error('스크립트는 로드되었지만 VWorld API를 찾을 수 없습니다.');
              setMapLoaded(true);
            }
          }, 500); // 약간의 지연 후 초기화
        };
        
        script.onerror = (error) => {
          console.error('스크립트 로드 실패:', error);
          setMapLoaded(true);
        };
        
        // 이미 로드된 경우
        if (script.complete) {
          console.log('스크립트 이미 완료됨, 지도 초기화 시도');
          setTimeout(() => {
            if (window.vw && window.vw.ol3) {
              initMap();
            } else {
              console.warn('스크립트는 완료되었지만 VWorld API를 찾을 수 없습니다.');
              // 재시도
              setTimeout(initMap, 500);
            }
          }, 500);
        }
      }
    };

    checkAndInit();

    return () => {
      if (mapInstanceRef.current) {
        // VWorld Map 정리
        try {
          mapInstanceRef.current.destroy();
        } catch (e) {
          console.warn('Error destroying VWorld map:', e);
        }
        mapInstanceRef.current = null;
        vectorSourceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [VWORLD_API_KEY, VWORLD_DOMAIN]);

  // plants 변경 시 마커 업데이트 (지도 초기화 완료 후에도 실행)
  useEffect(() => {
    if (!mapInstanceRef.current || !vectorSourceRef.current || !mapLoaded) {
      console.log('마커 업데이트 대기 중:', {
        mapInstance: !!mapInstanceRef.current,
        vectorSource: !!vectorSourceRef.current,
        mapLoaded: mapLoaded,
        plantsCount: plants?.length || 0
      });
      return;
    }

    console.log('마커 업데이트 시작:', {
      plantsCount: plants?.length || 0,
      plants: plants
    });

    const vectorSource = vectorSourceRef.current;

    // 마커 스타일 생성 함수
    const createMarkerStyle = (color, isSelected = false) => {
      return new Style({
        image: new Circle({
          radius: isSelected ? 12 : 10,
          fill: new Fill({ color }),
          stroke: new Stroke({
            color: 'white',
            width: isSelected ? 3 : 2,
          }),
        }),
        text: new Text({
          text: '⚡',
          scale: isSelected ? 1.2 : 1,
          fill: new Fill({ color: 'white' }),
          offsetY: 2,
        }),
      });
    };

    // 기존 마커 제거
    vectorSource.clear();
    markersRef.current = [];

    // 새로운 마커 추가
    if (plants && plants.length > 0) {
      let addedCount = 0;
      plants.forEach(plant => {
        if (!plant.location || !plant.location.lat || !plant.location.lng) {
          console.warn('발전소 위치 정보가 없습니다:', plant);
          return;
        }

        try {
          const coordinates = fromLonLat([plant.location.lng, plant.location.lat], 'EPSG:3857');
          const feature = new Feature({
            geometry: new Point(coordinates),
            plant: plant,
          });

          const isSelected = selectedPlant?.id === plant.id;
          feature.setStyle(createMarkerStyle(getStatusColor(plant.status), isSelected));

          vectorSource.addFeature(feature);
          markersRef.current.push(feature);
          addedCount++;
        } catch (error) {
          console.error('마커 추가 오류:', error, plant);
        }
      });
      console.log(`마커 추가 완료: ${addedCount}개`);
    } else {
      console.log('표시할 발전소가 없습니다.');
    }
  }, [plants, selectedPlant, getStatusColor, mapLoaded]); // mapLoaded 추가하여 지도 초기화 완료 후에도 실행

  // 전체 보기로 이동하는 함수
  const resetToInitialView = useCallback(() => {
    if (!mapInstanceRef.current) return;
    const vmap = mapInstanceRef.current;
    const olMap = vmap.getMap();
    const coordinates = fromLonLat(INITIAL_CENTER, 'EPSG:3857');
    const initialZoom = isMobile() ? INITIAL_ZOOM_MOBILE : INITIAL_ZOOM;
    olMap.getView().setCenter(coordinates);
    olMap.getView().setZoom(initialZoom);
  }, []);

  // 선택된 발전소 변경 시 마커 스타일 업데이트 및 지도 이동
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const vmap = mapInstanceRef.current;
    const olMap = vmap.getMap();

    // 마커 스타일 업데이트
    if (markersRef.current.length > 0) {
      markersRef.current.forEach(feature => {
        const plant = feature.get('plant');
        const isSelected = selectedPlant?.id === plant.id;
        const style = new Style({
          image: new Circle({
            radius: isSelected ? 12 : 10,
            fill: new Fill({ color: getStatusColor(plant.status) }),
            stroke: new Stroke({
              color: 'white',
              width: isSelected ? 3 : 2,
            }),
          }),
          text: new Text({
            text: '⚡',
            scale: isSelected ? 1.2 : 1,
            fill: new Fill({ color: 'white' }),
            offsetY: 2,
          }),
        });
        feature.setStyle(style);
      });
    }

    // 선택된 발전소로 지도 이동
    if (selectedPlant && selectedPlant.location) {
      const coordinates = fromLonLat([
        selectedPlant.location.lng,
        selectedPlant.location.lat,
      ], 'EPSG:3857');
      olMap.getView().setCenter(coordinates);
      olMap.getView().setZoom(16); // 더 확대된 줌 레벨 (13 -> 16)
    } else {
      // selectedPlant가 null일 때는 초기 좌표로 이동
      resetToInitialView();
    }
  }, [selectedPlant, getStatusColor, resetToInitialView]);

  return (
    <div className="vworld-map">
      <div className="map-header">
        <div className="map-title-section">
          <h3>발전소 위치</h3>
        </div>
        <div className="map-controls">
          <button
            className="reset-view-button"
            onClick={() => {
              onPlantSelect(null);
              resetToInitialView();
            }}
            title="전체 보기"
          >
            전체 보기
          </button>
        </div>
        <div className="map-legend">
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#10b981' }}></div>
            <span>정상</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#f59e0b' }}></div>
            <span>부분 오류</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: '#ef4444' }}></div>
            <span>통신 오류</span>
          </div>
        </div>
      </div>

      <div className="map-container" style={{ position: 'relative' }}>
        <div 
          ref={mapRef} 
          id="vworld-map"
          className="vworld-map-container" 
          style={{ width: '100%', height: '100%' }}
        />
        {!mapLoaded && (
          <div className="map-loading" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 1000
          }}>
            <div className="loading-spinner"></div>
            <p>지도를 불러오는 중...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VWorldMap;
